// Pure decision helpers extracted from the useChat Zustand store so the real
// logic can be unit-tested directly (instead of re-implemented copies). Mirrors
// the own-read-cache.ts pattern: no set/get/api/ws deps — imported by both the
// store call sites and the tests. Reuses isRealPlaintext from safeMessageText.
import { isRealPlaintext } from "./safeMessageText";
import type { Message, Channel } from "../hooks/useChat";

/**
 * Merge `incoming` messages into `existing` by id. Never regress an already-
 * decrypted plaintext to a placeholder: when a known message arrives as a
 * placeholder but we already hold real text, keep the real text (and its
 * fallbackMode). Does NOT sort — callers sort by createdAt.
 */
export function mergeMessagesPreservingPlaintext<
  T extends { id: string; plaintext?: string | null; fallbackMode?: "plaintext" },
>(existing: T[], incoming: T[]): T[] {
  const merged = new Map<string, T>();
  for (const m of existing) merged.set(m.id, m);
  for (const m of incoming) {
    const prev = merged.get(m.id);
    if (prev && isRealPlaintext(prev.plaintext) && !isRealPlaintext(m.plaintext)) {
      merged.set(m.id, {
        ...m,
        plaintext: prev.plaintext,
        ...(prev.fallbackMode ? { fallbackMode: prev.fallbackMode } : {}),
      });
    } else {
      merged.set(m.id, m);
    }
  }
  return [...merged.values()];
}

/**
 * Count peers' freshly-decrypted, readable messages — skip own messages and
 * undecryptable placeholders so a recurring decrypt error never re-marks unread.
 */
export function countRealUnread(
  decrypted: Pick<Message, "senderId" | "plaintext">[],
  currentUserId?: string,
): number {
  return decrypted.filter(
    (m) => (!currentUserId || m.senderId !== currentUserId) && isRealPlaintext(m.plaintext),
  ).length;
}

/**
 * New unread count after a live message arrives. No bump for the active channel,
 * an own-device message, or an undecryptable placeholder. Returns the previous
 * value unchanged (incl. undefined) in the no-bump case.
 */
export function bumpUnread(
  prev: number | undefined,
  message: Pick<Message, "plaintext">,
  opts: { isActiveChannel: boolean; fromSelf: boolean },
): number | undefined {
  return opts.isActiveChannel || opts.fromSelf || !isRealPlaintext(message.plaintext)
    ? prev
    : (prev ?? 0) + 1;
}

/**
 * True when a channel's single unread message decrypted to a placeholder — the
 * badge is stale and should be cleared + server-acked. Gated to unreadCount===1:
 * with only lastMessage decrypted we can only prove the whole tail is a
 * placeholder when there is exactly one unread.
 */
export function isPlaceholderTail(
  ch: Pick<Channel, "unreadCount">,
  decryptedPlaintext: string,
  hasId: boolean,
): boolean {
  return (ch.unreadCount ?? 0) === 1 && !isRealPlaintext(decryptedPlaintext) && hasId;
}

/**
 * Reducer for the channel_deleted broadcast: remove the channel and its messages
 * from the store, and clear activeChannelId if it pointed at the deleted channel.
 */
export function applyChannelDeleted(
  state: {
    channels: Channel[];
    messages: Map<string, Message[]>;
    activeChannelId: string | null;
  },
  channelId: string,
): { channels: Channel[]; messages: Map<string, Message[]>; activeChannelId: string | null } {
  const messages = new Map(state.messages);
  messages.delete(channelId);
  return {
    channels: state.channels.filter((ch) => ch.id !== channelId),
    messages,
    activeChannelId: state.activeChannelId === channelId ? null : state.activeChannelId,
  };
}
